import type { ShopifyPlanCommitment } from "@intelligems/ig-types";

import { committedEntityIds } from "./catalog";
import { SMART_PRICING_VARIANTS, UNLIMITED_ID } from "./constants";
import { isSelectionNoOp } from "./selection";
import type { BillingSnapshot, Selection, SelectionState } from "./types";

/**
 * A Shopify free trial with no AppSubscription behind it, read from the same
 * trial the Plan Commitment is resolved against. A trial that predates Plan
 * Commitments has no such trial and keeps the old approval flow.
 */
export const isBridgeOnlyTrial = (snapshot: BillingSnapshot): boolean =>
  snapshot.subscriptionAccessData.shopifyTrial?.state === "trialing";

/**
 * Smart Pricing and Unlimited carry no trial days, so choosing either ends the
 * trial once Shopify approves it instead of waiting for the trial's end.
 */
export const selectionEndsTrial = (selection: Selection): boolean =>
  selection.planId === UNLIMITED_ID ||
  selection.moduleIds.some((id) => SMART_PRICING_VARIANTS.has(id));

/**
 * Whether a commitment applies as soon as Shopify approves it, ending the
 * trial, instead of at the trial's end. Its `startsAt` is the trial's end
 * either way, so that date is not when such a plan starts.
 */
export const commitmentEndsTrial = (
  planCommitment: Pick<ShopifyPlanCommitment, "moduleIds" | "moduleNames">
): boolean =>
  committedEntityIds(planCommitment).some(
    (id) => id === UNLIMITED_ID || SMART_PRICING_VARIANTS.has(id)
  );

export const planCommitmentSelection = (
  snapshot: BillingSnapshot
): Selection | null => {
  const { planCommitment } = snapshot.subscriptionAccessData;
  if (!planCommitment) {
    return null;
  }

  const entityIds = committedEntityIds(planCommitment);
  return {
    ...(entityIds.includes(UNLIMITED_ID) ? { planId: UNLIMITED_ID } : {}),
    moduleIds: entityIds.filter((id) => id !== UNLIMITED_ID),
    cadence: planCommitment.cadence === "annual" ? "year" : "month",
  };
};

/**
 * Resubmitting a commitment Shopify has not approved is how a merchant retries
 * a pending, declined, or expired approval, so only an approved one is a no-op.
 */
export const isPlanCommitmentSelection = (
  snapshot: BillingSnapshot,
  selection: SelectionState
): boolean => {
  const committed = planCommitmentSelection(snapshot);
  return (
    committed !== null &&
    snapshot.subscriptionAccessData.planCommitment?.providerApproval ===
      "approved" &&
    committed.cadence === selection.cadence &&
    isSelectionNoOp(selection, committed)
  );
};

/**
 * Whether submitting during a Bridge-Only Trial is certain to send the merchant
 * to Shopify. Whether a change to an approved commitment needs a fresh approval
 * depends on the backend's cap, so the page does not predict it.
 */
export const trialSubmitNeedsShopify = (snapshot: BillingSnapshot): boolean =>
  snapshot.subscriptionAccessData.planCommitment?.providerApproval !==
  "approved";
